import { motion, AnimatePresence } from "framer-motion";
import {
  FiPlus,
  FiMinus,
  FiTrash2,
  FiCalendar,
  FiCreditCard,
  FiGift,
  FiUser,
  FiMail,
  FiPhone,
  FiCheck,
  FiX,
  FiChevronDown,
  FiChevronUp,
} from "react-icons/fi";
import { useLocation, useNavigate } from "react-router-dom";
import useCartStore from "../store/cartStore";
import { useSubscriptionStore } from "../store/subscriptionStore.js";
import axiosToken from "../api/axiosClient";
import { toast } from "react-toastify";
import { useState, useEffect, useRef } from "react";
import ReservationServiceCalendar from "../components/ReservationServiceCalendar";
import useAuthStore from "../store/authStore";
import { formatPhoneNumber } from "../functions/formatPhoneNumberFunctions.js";

function CartPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const currentPath = location.pathname;

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isEditingDate, setIsEditingDate] = useState(false);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [showFedapayModal, setShowFedapayModal] = useState(false);
  const [showGiftCardModal, setShowGiftCardModal] = useState(false);
  const [showSubscriptionModal, setShowSubscriptionModal] = useState(false);
  const [showConfirmClearModal, setShowConfirmClearModal] = useState(false);
  const [phoneNumber, setPhoneNumber] = useState("");
  const [reservationId, setReservationId] = useState(null);

  // États pour cartes cadeaux
  const [giftCardCode, setGiftCardCode] = useState("");
  const [giftCardData, setGiftCardData] = useState(null);
  const [giftCardLoading, setGiftCardLoading] = useState(false);
  const [giftCardDate, setGiftCardDate] = useState(null);
  const [showGiftCardCalendar, setShowGiftCardCalendar] = useState(false);

  // États pour abonnements
  const [subscriptionCode, setSubscriptionCode] = useState("");
  const [subscriptionData, setSubscriptionData] = useState(null);
  const [subscriptionLoading, setSubscriptionLoading] = useState(false);
  const [subscriptionDate, setSubscriptionDate] = useState(null);
  const [showSubscriptionCalendar, setShowSubscriptionCalendar] =
    useState(false);
  const [selectedSubscription, setSelectedSubscription] = useState(null);
  const [selectedSubscriptionService, setSelectedSubscriptionService] =
    useState(null);
  const [selectedSubscriptionServiceCode, setSelectedSubscriptionServiceCode] =
    useState("");

  // États pour utilisateurs anonymes
  const [anonymousNom, setAnonymousNom] = useState("");
  const [anonymousPrenom, setAnonymousPrenom] = useState("");
  const [anonymousEmail, setAnonymousEmail] = useState("");
  const [anonymousPhone, setAnonymousPhone] = useState("");
  const [currentAction, setCurrentAction] = useState(null); // "giftcard" ou "subscription"

  const {
    items,
    selectedDate,
    updateDate,
    removeFromCart,
    updateQuantity,
    clearCart,
    getTotalPrice,
    getReservationData,
    isEmpty,
  } = useCartStore();
  const { user, isLoading, getUser, logout } = useAuthStore();

  const { fetchUserSubscriptions, userSubscriptions, loading } =
    useSubscriptionStore();

  const [fedapayLoaded, setFedapayLoaded] = useState(false);

  const giftCardInputRef = useRef(null);
  const subscriptionInputRef = useRef(null);

  useEffect(() => {
    // Charger dynamiquement le script FedaPay
    const script = document.createElement("script");
    script.src = "https://cdn.fedapay.com/checkout.js?v=1.1.7";
    script.onload = () => setFedapayLoaded(true);
    script.onerror = () => toast.error("Erreur de chargement de FedaPay");
    document.body.appendChild(script);
  }, []);

  useEffect(() => {
    fetchUserSubscriptions();
  }, [fetchUserSubscriptions]);

  useEffect(() => {
    if (user) {
      setAnonymousNom(user.nom || "");
      setAnonymousPrenom(user.prenom || "");
      setAnonymousPhone(user.telephone || "");
      setAnonymousEmail(user.email || "");
    }
  }, [user]);

  const handleDateUpdate = (newDate) => {
    updateDate(newDate);
    setIsEditingDate(false);
    toast.success("Date de réservation mise à jour");
  };

  const handleGiftCardDateUpdate = (newDate) => {
    setGiftCardDate(newDate);
    setShowGiftCardCalendar(false);
  };

  const handleSubscriptionDateUpdate = (newDate) => {
    setSubscriptionDate(newDate);
    setShowSubscriptionCalendar(false);
  };

  // Fonction pour vérifier une carte cadeau
  const verifyGiftCard = async () => {
    if (!giftCardCode.trim()) {
      toast.error("Veuillez entrer un code de carte cadeau");
      return;
    }

    try {
      setGiftCardLoading(true);
      const response = await axiosToken.get(
        `/gift-card/service/code/${giftCardCode}`,
      );

      if (response.data.success) {
        if (response.data.giftCard.used) {
          toast.error("Cette carte cadeau a déjà été utilisée");
          setGiftCardData(null);
        } else {
          setGiftCardData(response.data);
          toast.success("Carte cadeau valide !");
        }
      }
    } catch (error) {
      console.error("Erreur vérification carte cadeau:", error);
      toast.error(
        error.response?.data?.error || "Code de carte cadeau invalide",
      );
      setGiftCardData(null);
    } finally {
      setGiftCardLoading(false);
    }
  };

  // Fonction pour vérifier un abonnement (pour utilisateurs anonymes)
  const verifySubscription = async () => {
    if (!subscriptionCode.trim()) {
      toast.error("Veuillez entrer un code d'abonnement");
      return;
    }

    try {
      setSubscriptionLoading(true);
      const response = await axiosToken.get(
        `/subscriptions/service/code/${subscriptionCode}`,
      );

      if (response.data.success) {
        if (response.data.subscriptionService.used) {
          toast.error("Cet abonnement a déjà été utilisé");
          setSubscriptionData(null);
        } else {
          setSubscriptionData(response.data);
          toast.success("Abonnement valide !");
        }
      }
    } catch (error) {
      console.error("Erreur vérification abonnement:", error);
      toast.error(error.response?.data?.error || "Code d'abonnement invalide");
      setSubscriptionData(null);
    } finally {
      setSubscriptionLoading(false);
    }
  };

  // Fonction pour gérer le choix de paiement
  const handlePaymentChoice = async (payNow = false) => {
    setShowPaymentModal(false);

    if (payNow) {
      setShowFedapayModal(true);
    } else {
      await processReservation(false);
    }
  };

  // Initialiser FedaPay
  const initializeFedapayPayment = async () => {
    try {
      if (!fedapayLoaded || !window.FedaPay) {
        toast.error("FedaPay n'est pas encore prêt. Réessayez.");
        return;
      }

      const reservationData = getReservationData();
      const descriptionLabel = reservationData.serviceIds
        .map((item) => `#${item.serviceId}*${item.quantity}`)
        .join(", ");

      const newWidget = window.FedaPay.init({
        public_key: process.env.REACT_APP_FEDAPAY_PUBLIC_KEY,
        transaction: {
          amount: getTotalPrice(),
          description: `Réservation - ${descriptionLabel}`,
        },
        customer: {
          email: user ? user.email : anonymousEmail,
          lastname: user ? user.nom : anonymousNom,
          firstname: user ? user.prenom : anonymousPrenom,
          phone_number: formatPhoneNumber(
            user ? user.telephone : anonymousPhone,
          ),
        },
        ui: {
          theme: "light",
          display_mode: "popup",
        },
        onComplete: async ({ reason, transaction }) => {
          if (transaction.status !== "approved") {
            toast.error(reason);
            setIsSubmitting(false);
          } else {
            console.log("Paiement réussi:", transaction);
            await processReservation(true, transaction);
            setIsSubmitting(false);
          }
        },
      });

      await newWidget.open();
    } catch (error) {
      console.error("Erreur d'initialisation FedaPay :", error);
      toast.error("Erreur FedaPay : " + error.message);
    }
  };

  // Réserver avec carte cadeau
  const processGiftCardReservation = async () => {
    if (!giftCardDate) {
      toast.error("Veuillez sélectionner une date");
      return;
    }

    if (
      !user &&
      (!anonymousNom || !anonymousPrenom || !anonymousEmail || !anonymousPhone)
    ) {
      toast.error("Veuillez remplir tous les champs");
      return;
    }

    try {
      setIsSubmitting(true);

      const reservationData = {
        date: giftCardDate,
        giftCard_code: giftCardCode,
      };

      if (user) {
        // Utilisateur connecté
        await axiosToken.post("/reservation", reservationData);
      } else {
        // Utilisateur anonyme
        reservationData.email = anonymousEmail;
        reservationData.name = `${anonymousNom} ${anonymousPrenom}`;
        reservationData.telephone = anonymousPhone;

        await axiosToken.post("/reservation/anonymous", reservationData);
      }

      toast.success("Réservation avec carte cadeau effectuée avec succès !");

      // Réinitialiser les états
      setGiftCardCode("");
      setGiftCardData(null);
      setGiftCardDate(null);
      setAnonymousNom("");
      setAnonymousPrenom("");
      setAnonymousEmail("");
      setAnonymousPhone("");
      setShowGiftCardModal(false);

      // Rediriger vers les réservations
      setTimeout(() => {
        // Client connecté : ses rendez-vous ; visiteur anonyme : accueil
        navigate(user ? "/rendez-vous" : "/");
      }, 1500);
    } catch (error) {
      console.error("Erreur de réservation avec carte cadeau:", error);
      toast.error(
        error.response?.data?.error || "Erreur lors de la réservation",
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  // Réserver avec abonnement
  const processSubscriptionReservation = async () => {
    if (!subscriptionDate) {
      toast.error("Veuillez sélectionner une date");
      return;
    }

    if (
      !user &&
      (!anonymousNom || !anonymousPrenom || !anonymousEmail || !anonymousPhone)
    ) {
      toast.error("Veuillez remplir tous les champs");
      return;
    }

    const subscriptionServiceCode = user
      ? selectedSubscriptionServiceCode
      : subscriptionCode;

    if (!subscriptionServiceCode) {
      toast.error("Veuillez sélectionner un service d'abonnement");
      return;
    }

    try {
      setIsSubmitting(true);

      const reservationData = {
        date: subscriptionDate,
        subscriptionService_code: subscriptionServiceCode,
      };

      if (user) {
        // Utilisateur connecté
        await axiosToken.post("/reservation", reservationData);
      } else {
        // Utilisateur anonyme
        reservationData.email = anonymousEmail;
        reservationData.name = `${anonymousNom} ${anonymousPrenom}`;
        reservationData.telephone = anonymousPhone;

        await axiosToken.post("/reservation/anonymous", reservationData);
      }

      toast.success("Réservation avec abonnement effectuée avec succès !");

      // Réinitialiser les états
      setSubscriptionCode("");
      setSubscriptionData(null);
      setSubscriptionDate(null);
      setSelectedSubscription(null);
      setSelectedSubscriptionService(null);
      setSelectedSubscriptionServiceCode("");
      setAnonymousNom("");
      setAnonymousPrenom("");
      setAnonymousEmail("");
      setAnonymousPhone("");
      setShowSubscriptionModal(false);

      // Rediriger vers les réservations
      setTimeout(() => {
        // Client connecté : ses rendez-vous ; visiteur anonyme : accueil
        navigate(user ? "/rendez-vous" : "/");
      }, 1500);
    } catch (error) {
      console.error("Erreur de réservation avec abonnement:", error);
      toast.error(
        error.response?.data?.error || "Erreur lors de la réservation",
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  // Fonction pour traiter la réservation sans paiement
  const processReservation = async (paid = false, transaction) => {
    if (!selectedDate) {
      toast.error("Veuillez sélectionner une date de réservation");
      return;
    }

    // Validation pour utilisateurs anonymes
    if (
      !user &&
      (!anonymousNom || !anonymousPrenom || !anonymousEmail || !anonymousPhone)
    ) {
      toast.error("Veuillez remplir tous les champs");
      return;
    }

    try {
      setIsSubmitting(true);
      const reservationData = getReservationData();

      if (!reservationData) {
        toast.error("Votre panier est vide");
        return;
      }

      let reservationWithPayment;
      if (transaction) {
        reservationWithPayment = {
          ...reservationData,
          transaction_id: transaction ? transaction.id : "",
        };
      } else {
        reservationWithPayment = {
          ...reservationData,
        };
      }

      reservationWithPayment.email = anonymousEmail;
      reservationWithPayment.name = `${anonymousNom} ${anonymousPrenom}`;
      reservationWithPayment.telephone = anonymousPhone;

      if (user) {
        // Utilisateur connecté
        await axiosToken.post("/reservation", reservationWithPayment);
      } else {
        // Utilisateur anonyme

        await axiosToken.post("/reservation/anonymous", reservationWithPayment);
      }

      toast.success("Réservation effectuée avec succès !");

      clearCart();

      setTimeout(() => {
        // Client connecté : ses rendez-vous ; visiteur anonyme : accueil
        navigate(user ? "/rendez-vous" : "/");
      }, 1500);
    } catch (error) {
      console.error("Erreur de réservation :", error);
      toast.error(
        error?.response?.data?.error || "Erreur lors de la réservation",
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  // Fonction pour afficher le modal de choix
  const handleReservation = () => {
    setShowPaymentModal(true);
  };

  useEffect(() => {
    if (selectedSubscriptionServiceCode) {
      const service = getAvailableSubscriptionServices().filter(
        (el) => el.code === selectedSubscriptionServiceCode,
      );
      setSelectedSubscriptionService(service[0]);
    }
  }, [selectedSubscriptionServiceCode]);

  // Ouvrir modal carte cadeau avec vérification du panier
  const openGiftCardModal = () => {
    console.log("Opening gift card modal, isEmpty:", isEmpty()); // Debug
    if (!isEmpty()) {
      console.log("Cart is not empty, showing confirmation modal"); // Debug
      setCurrentAction("giftcard");
      setShowConfirmClearModal(true);
    } else {
      console.log("Cart is empty, showing gift card modal directly"); // Debug
      setShowGiftCardModal(true);
    }
  };

  // Ouvrir modal abonnement avec vérification du panier
  const openSubscriptionModal = () => {
    console.log("Opening subscription modal, isEmpty:", isEmpty()); // Debug
    if (!isEmpty()) {
      console.log("Cart is not empty, showing confirmation modal"); // Debug
      setCurrentAction("subscription");
      setShowConfirmClearModal(true);
    } else {
      console.log("Cart is empty, showing subscription modal directly"); // Debug
      setShowSubscriptionModal(true);
    }
  };

  // Confirmer la suppression du panier
  const confirmClearCartAndContinue = () => {
    clearCart();
    setShowConfirmClearModal(false);

    if (currentAction === "giftcard") {
      setShowGiftCardModal(true);
    } else if (currentAction === "subscription") {
      setShowSubscriptionModal(true);
    }
  };

  // Filtrer les services d'abonnement non utilisés
  const getAvailableSubscriptionServices = () => {
    if (!selectedSubscription || !selectedSubscription.services) return [];
    return selectedSubscription.services.filter((service) => !service.used);
  };

  // Render content based on cart state
  const renderContent = () => {
    if (isEmpty()) {
      return (
        <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 flex items-center justify-center px-4">
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            className="text-center max-w-md"
          >
            <div className="w-32 h-32 bg-gradient-to-br from-primary to-secondary flex items-center justify-center mx-auto mb-8 shadow-xl">
              <svg
                className="w-16 h-16 text-white"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={1.5}
                  d="M3 3h2l.4 2M7 13h10l4-8H5.4M7 13L5.4 5M7 13l-2.293 2.293c-.63.63-.184 1.707.707 1.707H17m0 0a2 2 0 100 4 2 2 0 000-4zm-8 2a2 2 0 11-4 0 2 2 0 014 0z"
                />
              </svg>
            </div>
            <h2 className="font-poppins font-bold text-[24px] text-gray-800 mb-4 bg-gradient-to-r from-primary to-secondary bg-clip-text text-transparent">
              Votre panier est vide
            </h2>
            <p className="font-nunito text-[16px] text-gray-600 mb-8">
              Ajoutez des services à votre panier pour commencer vos
              réservations ou utilisez un abonnement ou une carte cadeau.
            </p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
              <button
                onClick={() => navigate("/services")}
                className="px-6 py-3 bg-gradient-to-r from-primary to-secondary text-white hover:opacity-90 transition-all duration-300 font-nunito font-bold text-[15px] shadow-lg hover:shadow-xl"
              >
                Explorer les services
              </button>
              <button
                onClick={openGiftCardModal}
                className="px-6 py-3 bg-gradient-to-r from-purple-500 to-purple-700 text-white hover:opacity-90 transition-all duration-300 font-nunito font-bold text-[15px] shadow-lg hover:shadow-xl flex items-center justify-center gap-2"
              >
                <FiGift size={18} />
                Carte cadeau
              </button>
            </div>
            <button
              onClick={openSubscriptionModal}
              className="w-full px-6 py-3 bg-gradient-to-r from-blue-500 to-blue-700 text-white hover:opacity-90 transition-all duration-300 font-nunito font-bold text-[15px] shadow-lg hover:shadow-xl flex items-center justify-center gap-2"
            >
              <FiCreditCard size={18} />
              Réserver avec abonnement
            </button>
          </motion.div>
        </div>
      );
    }

    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 py-12">
        <div className="max-w-6xl mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-white shadow-2xl overflow-hidden"
          >
            {/* En-tête avec dégradé */}
            <div className="bg-gradient-to-r from-primary to-secondary p-8">
              <h1 className="font-poppins font-bold text-[28px] text-white">
                Votre panier
              </h1>
              <p className="font-nunito text-[16px] text-white/90 mt-2">
                {items.length} service{items.length > 1 ? "s" : ""} sélectionné
                {items.length > 1 ? "s" : ""}
              </p>
            </div>

            <div className="p-8">
              {/* Section Options de réservation */}
              <div className="mb-8 p-6 bg-gradient-to-r from-gray-50 to-white border border-gray-200 shadow">
                <h3 className="font-poppins font-bold text-[20px] text-gray-800 mb-4">
                  Options de réservation
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <button
                    onClick={openGiftCardModal}
                    className="p-4 bg-gradient-to-r from-purple-50 to-white border-2 border-purple-200 hover:border-purple-400 hover:shadow-lg transition-all duration-300 group"
                  >
                    <div className="flex items-center gap-3">
                      <div className="w-12 h-12 bg-gradient-to-br from-purple-500 to-purple-700 flex items-center justify-center">
                        <FiGift className="text-white text-xl" />
                      </div>
                      <div className="text-left">
                        <h4 className="font-poppins font-bold text-[16px] text-gray-800 group-hover:text-purple-600">
                          Carte cadeau
                        </h4>
                        <p className="font-nunito text-[14px] text-gray-600 mt-1">
                          Utiliser une carte cadeau
                        </p>
                      </div>
                    </div>
                  </button>
                  <button
                    onClick={openSubscriptionModal}
                    className="p-4 bg-gradient-to-r from-blue-50 to-white border-2 border-blue-200 hover:border-blue-400 hover:shadow-lg transition-all duration-300 group"
                  >
                    <div className="flex items-center gap-3">
                      <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-blue-700 flex items-center justify-center">
                        <FiCreditCard className="text-white text-xl" />
                      </div>
                      <div className="text-left">
                        <h4 className="font-poppins font-bold text-[16px] text-gray-800 group-hover:text-blue-600">
                          Abonnement
                        </h4>
                        <p className="font-nunito text-[14px] text-gray-600 mt-1">
                          Utiliser votre abonnement
                        </p>
                      </div>
                    </div>
                  </button>
                </div>
              </div>

              {/* Section Date de réservation */}
              <div className="mb-8 p-6 bg-gradient-to-r from-secondary/10 to-primary/10 border border-secondary/30 shadow">
                <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between mb-4">
                  <div className="flex items-center gap-4 mb-4 sm:mb-0">
                    <div className="w-14 h-14 bg-gradient-to-br from-black to-gray-800 flex items-center justify-center">
                      <FiCalendar className="text-white text-2xl" />
                    </div>
                    <div>
                      <h3 className="font-poppins font-bold text-[18px] text-gray-800">
                        Date de réservation
                      </h3>
                      {selectedDate ? (
                        <p className="font-poppins font-bold text-[18px] text-primary mt-1">
                          {selectedDate}
                        </p>
                      ) : (
                        <p className="font-poppins font-medium text-[16px] text-gray-500">
                          Aucune date sélectionnée
                        </p>
                      )}
                    </div>
                  </div>

                  <div className="flex gap-3">
                    {!isEditingDate ? (
                      <button
                        onClick={() => setIsEditingDate(true)}
                        className="px-6 py-3 bg-gradient-to-r from-black to-gray-800 text-white hover:opacity-90 transition-all duration-300 font-nunito font-bold text-[15px] shadow hover:shadow-lg"
                      >
                        {selectedDate ? "Modifier" : "Choisir"} la date
                      </button>
                    ) : (
                      <button
                        onClick={() => setIsEditingDate(false)}
                        className="px-6 py-3 bg-gradient-to-r from-gray-600 to-gray-800 text-white hover:opacity-90 transition-all duration-300 font-nunito font-bold text-[15px] shadow hover:shadow-lg"
                      >
                        Annuler
                      </button>
                    )}
                  </div>
                </div>

                {/* Calendrier pour modifier la date */}
                <AnimatePresence>
                  {isEditingDate && (
                    <motion.div
                      initial={{ opacity: 0, height: 0 }}
                      animate={{ opacity: 1, height: "auto" }}
                      exit={{ opacity: 0, height: 0 }}
                      className="mt-6 p-6 bg-white border border-gray-200 shadow"
                    >
                      <ReservationServiceCalendar
                        onContinue={handleDateUpdate}
                        servicesSelected={items[0]?.service}
                        hideHeader={true}
                      />
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>

              {/* Liste des services */}
              <div className="space-y-4 mb-6">
                <h3 className="font-poppins font-bold text-[18px] text-gray-800 mb-4">
                  Services sélectionnés
                </h3>
                {items.map((item, index) => (
                  <motion.div
                    key={item.service.id}
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: index * 0.1 }}
                    className="flex flex-col lg:flex-row lg:items-center justify-between p-4 bg-gradient-to-r from-white to-gray-50 border border-gray-200 hover:shadow-lg transition-all duration-300"
                  >
                    <div className="flex-1 mb-3 lg:mb-0">
                      <div className="flex items-start gap-3">
                        <div className="w-12 h-12 bg-gradient-to-br from-primary to-secondary flex items-center justify-center flex-shrink-0 shadow">
                          <span className="text-white font-bold text-base">
                            {item.service.nom.charAt(0)}
                          </span>
                        </div>
                        <div>
                          <h3 className="font-poppins font-bold text-[16px] text-gray-800">
                            {item.service.nom}
                          </h3>
                          <p className="font-nunito text-[13px] text-gray-600 mt-1">
                            {item.service.description}
                          </p>
                          <div className="flex items-center gap-4 mt-2">
                            <span className="font-poppins font-bold text-[15px] text-primary">
                              {item.service.price} FCFA
                            </span>
                          </div>
                        </div>
                      </div>
                    </div>

                    <div className="flex items-center gap-4">
                      {/* Contrôle de quantité */}
                      <div className="flex items-center gap-1 bg-gray-100 p-1 border border-gray-300">
                        <button
                          onClick={() =>
                            updateQuantity(item.service.id, item.quantity - 1)
                          }
                          className="w-7 h-7 flex items-center justify-center hover:bg-gray-200 transition-all duration-200"
                          disabled={item.quantity <= 1}
                        >
                          <FiMinus size={16} className="text-gray-700" />
                        </button>
                        <span className="font-poppins font-bold text-[15px] w-8 text-center text-gray-800">
                          {item.quantity}
                        </span>
                        <button
                          onClick={() =>
                            updateQuantity(item.service.id, item.quantity + 1)
                          }
                          className="w-7 h-7 flex items-center justify-center hover:bg-gray-200 transition-all duration-200"
                        >
                          <FiPlus size={16} className="text-gray-700" />
                        </button>
                      </div>

                      {/* Sous-total */}
                      <span className="font-poppins font-bold text-[15px] text-primary min-w-20 text-right">
                        {item.service.price * item.quantity} FCFA
                      </span>

                      {/* Supprimer */}
                      <button
                        onClick={() => removeFromCart(item.service.id)}
                        className="p-2 text-red-500 hover:bg-red-50 border border-red-200 hover:border-red-300 transition-all duration-200"
                        title="Supprimer du panier"
                      >
                        <FiTrash2 size={18} />
                      </button>
                    </div>
                  </motion.div>
                ))}
              </div>

              {/* Message d'alerte si pas de date */}
              {!selectedDate && (
                <motion.div
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="mb-6 p-6 bg-gradient-to-r from-yellow-50 to-orange-50 border border-yellow-300"
                >
                  <div className="flex items-start">
                    <div className="w-10 h-10 bg-gradient-to-br from-yellow-500 to-orange-500 flex items-center justify-center flex-shrink-0 mr-4">
                      <svg
                        className="w-6 h-6 text-white"
                        fill="currentColor"
                        viewBox="0 0 20 20"
                      >
                        <path
                          fillRule="evenodd"
                          d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z"
                          clipRule="evenodd"
                        />
                      </svg>
                    </div>
                    <div>
                      <p className="font-poppins font-bold text-[17px] text-yellow-800">
                        Date de réservation requise
                      </p>
                      <p className="font-nunito text-[15px] text-yellow-700 mt-1">
                        Vous devez sélectionner une date de réservation avant de
                        confirmer votre commande.
                      </p>
                    </div>
                  </div>
                </motion.div>
              )}

              {/* Résumé et actions */}
              <div className="border-t border-gray-300 pt-6">
                <div className="flex justify-between items-center mb-3">
                  <span className="font-nunito text-[14px] text-gray-600">
                    Sous-total:
                  </span>
                  <span className="font-poppins font-bold text-[15px] text-primary">
                    {getTotalPrice()} FCFA
                  </span>
                </div>

                <div className="flex justify-between items-center mb-6">
                  <span className="font-poppins font-bold text-[20px] text-gray-800">
                    Total:
                  </span>
                  <span className="font-poppins font-bold text-[20px] bg-gradient-to-r from-primary to-secondary bg-clip-text text-transparent">
                    {getTotalPrice()} FCFA
                  </span>
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-4 gap-3">
                  <button
                    onClick={clearCart}
                    className="px-4 py-3 border-2 border-gray-300 text-gray-700 hover:bg-gray-50 hover:border-gray-400 transition-all duration-300 font-nunito font-bold text-[14px] shadow hover:shadow-lg"
                  >
                    Vider le panier
                  </button>
                  <button
                    onClick={() => navigate("/services")}
                    className="px-4 py-3 border-2 border-secondary text-secondary hover:bg-secondary hover:text-white transition-all duration-300 font-nunito font-bold text-[14px] shadow hover:shadow-lg"
                  >
                    Continuer les achats
                  </button>
                  <button
                    onClick={openGiftCardModal}
                    className="px-4 py-3 bg-gradient-to-r from-purple-500 to-purple-700 text-white hover:opacity-90 transition-all duration-300 font-nunito font-bold text-[14px] shadow-lg hover:shadow-xl flex items-center justify-center gap-2"
                  >
                    <FiGift size={16} />
                    Carte cadeau
                  </button>
                  <button
                    onClick={handleReservation}
                    disabled={isSubmitting || !selectedDate}
                    className="px-4 py-3 bg-gradient-to-r from-primary to-secondary text-white hover:opacity-90 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-300 font-nunito font-bold text-[14px] shadow-lg hover:shadow-xl"
                  >
                    {isSubmitting ? (
                      <div className="flex items-center justify-center gap-2">
                        <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                        Traitement...
                      </div>
                    ) : (
                      `Confirmer la réservation`
                    )}
                  </button>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    );
  };

  return (
    <>
      {renderContent()}

      {/* Modal de confirmation de vidage du panier */}
      {showConfirmClearModal && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-[100] p-4">
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-white max-w-md w-full max-h-[40vh] flex flex-col shadow-2xl overflow-hidden"
          >
            {/* Header */}
            <div className="flex-none p-3 border-b flex items-center justify-center">
              <h3 className="font-poppins font-bold text-[15px] text-gray-800">
                Attention
              </h3>
            </div>

            {/* Content */}
            <div className="flex-1 overflow-y-auto p-3 flex flex-col items-center justify-center space-y-3">
              <div className="w-10 h-10 bg-gradient-to-br from-red-500 to-red-700 flex items-center justify-center">
                <FiTrash2 className="text-white text-lg" />
              </div>
              <p className="font-nunito font-light text-sm text-gray-600 text-center">
                Cette action videra votre panier en cours. Voulez-vous vraiment
                continuer ?
              </p>
            </div>

            {/* Footer */}
            <div className="flex-none p-3 border-t flex gap-2">
              <button
                onClick={() => setShowConfirmClearModal(false)}
                className="flex-1 px-4 py-2 border-2 border-gray-300 text-gray-700 hover:bg-gray-50 transition-all duration-300 font-nunito font-bold text-sm"
              >
                Annuler
              </button>
              <button
                onClick={confirmClearCartAndContinue}
                className="flex-1 px-4 py-2 bg-gradient-to-r from-red-500 to-red-700 text-white hover:opacity-90 transition-all duration-300 font-nunito font-bold text-sm"
              >
                Vider et continuer
              </button>
            </div>
          </motion.div>
        </div>
      )}

      {/* Modal Carte Cadeau */}
      {showGiftCardModal && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-[100] p-4">
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-white max-w-2xl w-full h-[80vh] flex flex-col shadow-2xl overflow-hidden"
          >
            {/* Header */}
            <div className="flex-none p-3 border-b flex items-center justify-between">
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 bg-gradient-to-br from-purple-500 to-purple-700 flex items-center justify-center">
                  <FiGift className="text-white text-sm" />
                </div>
                <h3 className="font-poppins font-bold text-[15px] text-gray-800">
                  Réserver avec carte cadeau
                </h3>
              </div>
              <button
                onClick={() => {
                  setShowGiftCardModal(false);
                  setGiftCardCode("");
                  setGiftCardData(null);
                  setGiftCardDate(null);
                }}
                className="p-1 hover:bg-gray-100 transition-all duration-200"
              >
                <FiX size={18} className="text-gray-500" />
              </button>
            </div>

            {/* Scrollable Content */}
            <div className="flex-1 overflow-y-auto p-3 space-y-4">
              {/* Étape 1: Code de carte cadeau */}
              <div className="p-3 bg-gradient-to-r from-purple-50 to-white border border-purple-200">
                <h4 className="font-poppins font-bold text-[15px] text-gray-800 mb-2">
                  1. Entrez votre code de carte cadeau
                </h4>
                <div className="flex flex-col space-y-2">
                  <input
                    ref={giftCardInputRef}
                    type="text"
                    value={giftCardCode}
                    onChange={(e) =>
                      setGiftCardCode(e.target.value.toUpperCase())
                    }
                    placeholder="Ex: CAD_1717181234567_A1B2_C001"
                    className="flex-1 p-2 border-2 border-purple-300 focus:border-purple-500 focus:outline-none font-nunito text-sm"
                    disabled={giftCardData}
                  />
                  <button
                    onClick={verifyGiftCard}
                    disabled={giftCardLoading || giftCardData}
                    className="px-4 py-2 bg-gradient-to-r from-purple-500 to-purple-700 text-white hover:opacity-90 disabled:opacity-50 transition-all duration-300 font-nunito font-bold text-sm whitespace-nowrap"
                  >
                    {giftCardLoading ? (
                      <div className="flex items-center gap-2">
                        <div className="w-3 h-3 border-2 border-white border-t-transparent rounded-full animate-spin" />
                        Vérification...
                      </div>
                    ) : giftCardData ? (
                      <div className="flex items-center gap-2">
                        <FiCheck size={16} />
                        Validé
                      </div>
                    ) : (
                      "Vérifier"
                    )}
                  </button>
                </div>
              </div>

              {/* Informations carte validée */}
              {giftCardData && (
                <motion.div
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="p-3 bg-gradient-to-r from-green-50 to-white border border-green-200"
                >
                  <div className="flex items-center gap-2">
                    <div className="w-8 h-8 bg-gradient-to-br from-green-500 to-green-700 flex items-center justify-center">
                      <FiCheck className="text-white text-base" />
                    </div>
                    <div>
                      <h4 className="font-poppins font-bold text-[14px] text-gray-800">
                        Carte cadeau valide
                      </h4>
                      <p className="font-nunito font-light text-[13px] text-green-700">
                        Service offert :{" "}
                        {giftCardData.giftCard.cadeauCard?.service?.nom}
                      </p>
                    </div>
                  </div>
                </motion.div>
              )}

              {/* Étape 2: Informations utilisateur (pour anonymes) */}
              {giftCardData && !user && (
                <div className="p-3 bg-gradient-to-r from-purple-50 to-white border border-purple-200">
                  <h4 className="font-poppins font-bold text-[15px] text-gray-800 mb-3">
                    2. Vos informations
                  </h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    <div>
                      <label className="font-nunito font-bold text-[13px] text-gray-700 block mb-1">
                        <FiUser className="inline mr-1" />
                        Nom
                      </label>
                      <input
                        type="text"
                        value={anonymousNom}
                        onChange={(e) => setAnonymousNom(e.target.value)}
                        placeholder="Votre nom"
                        className="w-full px-3 py-2 border-2 border-gray-300 focus:border-purple-500 focus:outline-none font-nunito text-sm"
                      />
                    </div>
                    <div>
                      <label className="font-nunito font-bold text-[13px] text-gray-700 block mb-1">
                        <FiUser className="inline mr-1" />
                        Prénom
                      </label>
                      <input
                        type="text"
                        value={anonymousPrenom}
                        onChange={(e) => setAnonymousPrenom(e.target.value)}
                        placeholder="Votre prénom"
                        className="w-full px-3 py-2 border-2 border-gray-300 focus:border-purple-500 focus:outline-none font-nunito text-sm"
                      />
                    </div>
                    <div>
                      <label className="font-nunito font-bold text-[13px] text-gray-700 block mb-1">
                        <FiMail className="inline mr-1" />
                        Email
                      </label>
                      <input
                        type="email"
                        value={anonymousEmail}
                        onChange={(e) => setAnonymousEmail(e.target.value)}
                        placeholder="votre@email.com"
                        className="w-full px-3 py-2 border-2 border-gray-300 focus:border-purple-500 focus:outline-none font-nunito text-sm"
                      />
                    </div>
                    <div>
                      <label className="font-nunito font-bold text-[13px] text-gray-700 block mb-1">
                        <FiPhone className="inline mr-1" />
                        Téléphone
                      </label>
                      <input
                        type="tel"
                        value={anonymousPhone}
                        onChange={(e) => setAnonymousPhone(e.target.value)}
                        placeholder="Votre numéro"
                        className="w-full px-3 py-2 border-2 border-gray-300 focus:border-purple-500 focus:outline-none font-nunito text-sm"
                      />
                    </div>
                  </div>
                </div>
              )}

              {/* Étape 3: Date de réservation */}
              {giftCardData &&
                (user ||
                  (anonymousNom &&
                    anonymousPrenom &&
                    anonymousEmail &&
                    anonymousPhone)) && (
                  <div className="p-3 bg-gradient-to-r from-secondary/5 to-primary/5 border border-secondary/20">
                    <div className="flex items-center justify-between">
                      <div>
                        <h4 className="font-poppins font-bold text-[15px] text-gray-800">
                          3. Choisissez la date
                        </h4>
                        {giftCardDate && (
                          <p className="font-poppins font-bold text-[13px] text-primary">
                            Date sélectionnée : {giftCardDate}
                          </p>
                        )}
                      </div>
                      <button
                        onClick={() =>
                          setShowGiftCardCalendar(!showGiftCardCalendar)
                        }
                        className="px-3 py-1 bg-gradient-to-r from-black to-gray-800 text-white hover:opacity-90 transition-all duration-300 font-nunito font-bold text-[12px] flex items-center gap-1"
                      >
                        {showGiftCardCalendar ? (
                          <FiChevronUp />
                        ) : (
                          <FiChevronDown />
                        )}
                        {showGiftCardCalendar ? "Masquer" : "Afficher"} le
                        calendrier
                      </button>
                    </div>

                    <AnimatePresence>
                      {showGiftCardCalendar && (
                        <motion.div
                          initial={{ opacity: 0, height: 0 }}
                          animate={{ opacity: 1, height: "auto" }}
                          exit={{ opacity: 0, height: 0 }}
                          className="mt-2 p-2 bg-white border border-gray-200 overflow-hidden"
                        >
                          <ReservationServiceCalendar
                            onContinue={handleGiftCardDateUpdate}
                            servicesSelected={
                              giftCardData.giftCard.cadeauCard?.service
                            }
                            hideHeader={true}
                          />
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </div>
                )}
            </div>

            {/* Fixed Footer */}
            <div className="flex-none p-3 border-t flex gap-3">
              <button
                onClick={() => {
                  setShowGiftCardModal(false);
                  setGiftCardCode("");
                  setGiftCardData(null);
                  setGiftCardDate(null);
                  setAnonymousNom("");
                  setAnonymousPrenom("");
                  setAnonymousEmail("");
                  setAnonymousPhone("");
                }}
                className="flex-1 px-4 py-2 border-2 border-gray-300 text-gray-700 hover:bg-gray-50 transition-all duration-300 font-nunito font-bold text-sm"
              >
                Annuler
              </button>
              <button
                onClick={processGiftCardReservation}
                disabled={
                  isSubmitting ||
                  !giftCardData ||
                  !giftCardDate ||
                  (!user &&
                    (!anonymousNom ||
                      !anonymousPrenom ||
                      !anonymousEmail ||
                      !anonymousPhone))
                }
                className="flex-[2] px-4 py-2 bg-gradient-to-r from-purple-500 to-purple-700 text-white hover:opacity-90 disabled:opacity-50 transition-all duration-300 font-nunito font-bold text-sm"
              >
                {isSubmitting ? (
                  <div className="flex items-center justify-center gap-2">
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                    En cours...
                  </div>
                ) : (
                  "Confirmer la réservation"
                )}
              </button>
            </div>
          </motion.div>
        </div>
      )}

      {/* Modal Abonnement */}
      {showSubscriptionModal && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-[100] p-4">
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-white max-w-2xl w-full h-[80vh] flex flex-col shadow-2xl overflow-hidden"
          >
            {/* Header */}
            <div className="flex-none p-3 border-b flex items-center justify-between">
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 bg-gradient-to-br from-blue-500 to-blue-700 flex items-center justify-center">
                  <FiCreditCard className="text-white text-sm" />
                </div>
                <h3 className="font-poppins font-bold text-[15px] text-gray-800">
                  Réserver avec abonnement
                </h3>
              </div>
              <button
                onClick={() => {
                  setShowSubscriptionModal(false);
                  setSubscriptionCode("");
                  setSubscriptionData(null);
                  setSubscriptionDate(null);
                  setSelectedSubscription(null);
                  setSelectedSubscriptionService(null);
                  setSelectedSubscriptionServiceCode("");
                }}
                className="p-1 hover:bg-gray-100 transition-all duration-200"
              >
                <FiX size={18} className="text-gray-500" />
              </button>
            </div>

            {/* Scrollable Content */}
            <div className="flex-1 overflow-y-auto p-3 space-y-4">
              {/* Pour utilisateurs connectés : Sélection d'abonnement */}
              {user ? (
                <div className="p-3 bg-gradient-to-r from-blue-50 to-white border border-blue-200">
                  <h4 className="font-poppins font-bold text-[15px] text-gray-800 mb-2">
                    1. Sélectionnez votre abonnement
                  </h4>

                  {loading ? (
                    <div className="text-center py-4">
                      <div className="w-6 h-6 border-4 border-blue-500 border-t-transparent rounded-full animate-spin mx-auto"></div>
                      <p className="font-nunito font-light text-sm text-gray-600 mt-2">
                        Chargement...
                      </p>
                    </div>
                  ) : userSubscriptions?.length > 0 ? (
                    <div className="space-y-2">
                      <select
                        value={selectedSubscription?.id || ""}
                        onChange={(e) => {
                          const subId = e.target.value;
                          const sub = userSubscriptions.find(
                            (s) => s.id == subId,
                          );
                          setSelectedSubscription(sub);
                          setSelectedSubscriptionService(null);
                          setSelectedSubscriptionServiceCode("");
                        }}
                        className="w-full px-3 py-2 border-2 border-blue-300 focus:border-blue-500 focus:outline-none font-nunito text-sm bg-white"
                      >
                        <option value="">Sélectionnez un abonnement</option>
                        {userSubscriptions.map((subscription) => (
                          <option key={subscription.id} value={subscription.id}>
                            {subscription.plan?.name} - {subscription.code}
                          </option>
                        ))}
                      </select>

                      {/* Sélection du service dans l'abonnement */}
                      {selectedSubscription && (
                        <div className="mt-2">
                          <label className="font-nunito font-bold text-[13px] text-gray-700 block mb-1">
                            2. Sélectionnez le service à utiliser
                          </label>
                          <select
                            value={selectedSubscriptionServiceCode}
                            onChange={(e) =>
                              setSelectedSubscriptionServiceCode(e.target.value)
                            }
                            className="w-full px-3 py-2 border-2 border-blue-300 focus:border-blue-500 focus:outline-none font-nunito text-sm bg-white"
                          >
                            <option value="">Sélectionnez un service</option>
                            {getAvailableSubscriptionServices().map(
                              (service) => (
                                <option key={service.id} value={service.code}>
                                  {service.service?.nom} -{" "}
                                  {service.used ? "Déjà utilisé" : "Disponible"}
                                </option>
                              ),
                            )}
                          </select>

                          {getAvailableSubscriptionServices().length === 0 && (
                            <p className="font-nunito font-light text-[12px] text-red-600 mt-1">
                              Aucun service disponible
                            </p>
                          )}
                        </div>
                      )}
                    </div>
                  ) : (
                    <div className="text-center py-4 bg-gray-50 border border-gray-200">
                      <p className="font-nunito font-light text-sm text-gray-600">
                        Vous n'avez pas d'abonnements actifs
                      </p>
                    </div>
                  )}
                </div>
              ) : (
                /* Pour utilisateurs anonymes : Saisie de code */
                <div className="p-3 bg-gradient-to-r from-blue-50 to-white border border-blue-200">
                  <h4 className="font-poppins font-bold text-[15px] text-gray-800 mb-2">
                    1. Entrez votre code d'abonnement
                  </h4>
                  <div className="flex flex-col space-y-2">
                    <input
                      ref={subscriptionInputRef}
                      type="text"
                      value={subscriptionCode}
                      onChange={(e) =>
                        setSubscriptionCode(e.target.value.toUpperCase())
                      }
                      placeholder="Ex: SUB_0000000000000_0a0b_S000"
                      className="flex-1 p-2 border-2 border-blue-300 focus:border-blue-500 focus:outline-none font-nunito text-sm"
                      disabled={subscriptionData}
                    />
                    <button
                      onClick={verifySubscription}
                      disabled={subscriptionLoading || subscriptionData}
                      className="px-4 py-2 bg-gradient-to-r from-blue-500 to-blue-700 text-white hover:opacity-90 disabled:opacity-50 transition-all duration-300 font-nunito font-bold text-sm whitespace-nowrap"
                    >
                      {subscriptionLoading ? (
                        <div className="flex items-center gap-2">
                          <div className="w-3 h-3 border-2 border-white border-t-transparent rounded-full animate-spin" />
                          Vérification...
                        </div>
                      ) : subscriptionData ? (
                        <div className="flex items-center gap-2">
                          <FiCheck size={16} />
                          Validé
                        </div>
                      ) : (
                        "Vérifier"
                      )}
                    </button>
                  </div>
                </div>
              )}

              {/* Informations abonnement validé */}
              {(subscriptionData || selectedSubscriptionServiceCode) && (
                <motion.div
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="p-3 bg-gradient-to-r from-green-50 to-white border border-green-200"
                >
                  <div className="flex items-center gap-2">
                    <div className="w-8 h-8 bg-gradient-to-br from-green-500 to-green-700 flex items-center justify-center">
                      <FiCheck className="text-white text-base" />
                    </div>
                    <div>
                      <h4 className="font-poppins font-bold text-[14px] text-gray-800">
                        Abonnement valide
                      </h4>
                      <p className="font-nunito font-light text-[13px] text-green-700">
                        {subscriptionData
                          ? `Service : ${subscriptionData.subscriptionService?.service?.nom}`
                          : `Service sélectionné`}
                      </p>
                    </div>
                  </div>
                </motion.div>
              )}

              {/* Étape 2: Informations utilisateur (pour anonymes) */}
              {(subscriptionData || selectedSubscriptionServiceCode) &&
                !user && (
                  <div className="p-3 bg-gradient-to-r from-blue-50 to-white border border-blue-200">
                    <h4 className="font-poppins font-bold text-[15px] text-gray-800 mb-3">
                      2. Vos informations
                    </h4>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                      <div>
                        <label className="font-nunito font-bold text-[13px] text-gray-700 block mb-1">
                          <FiUser className="inline mr-1" />
                          Nom
                        </label>
                        <input
                          type="text"
                          value={anonymousNom}
                          onChange={(e) => setAnonymousNom(e.target.value)}
                          placeholder="Votre nom"
                          className="w-full px-3 py-2 border-2 border-gray-300 focus:border-blue-500 focus:outline-none font-nunito text-sm"
                        />
                      </div>
                      <div>
                        <label className="font-nunito font-bold text-[13px] text-gray-700 block mb-1">
                          <FiUser className="inline mr-1" />
                          Prénom
                        </label>
                        <input
                          type="text"
                          value={anonymousPrenom}
                          onChange={(e) => setAnonymousPrenom(e.target.value)}
                          placeholder="Votre prénom"
                          className="w-full px-3 py-2 border-2 border-gray-300 focus:border-blue-500 focus:outline-none font-nunito text-sm"
                        />
                      </div>
                      <div>
                        <label className="font-nunito font-bold text-[13px] text-gray-700 block mb-1">
                          <FiMail className="inline mr-1" />
                          Email
                        </label>
                        <input
                          type="email"
                          value={anonymousEmail}
                          onChange={(e) => setAnonymousEmail(e.target.value)}
                          placeholder="votre@email.com"
                          className="w-full px-3 py-2 border-2 border-gray-300 focus:border-blue-500 focus:outline-none font-nunito text-sm"
                        />
                      </div>
                      <div>
                        <label className="font-nunito font-bold text-[13px] text-gray-700 block mb-1">
                          <FiPhone className="inline mr-1" />
                          Téléphone
                        </label>
                        <input
                          type="tel"
                          value={anonymousPhone}
                          onChange={(e) => setAnonymousPhone(e.target.value)}
                          placeholder="Votre numéro"
                          className="w-full px-3 py-2 border-2 border-gray-300 focus:border-blue-500 focus:outline-none font-nunito text-sm"
                        />
                      </div>
                    </div>
                  </div>
                )}

              {/* Étape 3: Date de réservation */}
              {(subscriptionData || selectedSubscriptionServiceCode) &&
                (user ||
                  (anonymousNom &&
                    anonymousPrenom &&
                    anonymousEmail &&
                    anonymousPhone)) && (
                  <div className="p-3 bg-gradient-to-r from-secondary/5 to-primary/5 border border-secondary/20">
                    <div className="flex items-center justify-between">
                      <div>
                        <h4 className="font-poppins font-bold text-[15px] text-gray-800">
                          3. Choisissez la date
                        </h4>
                        {subscriptionDate && (
                          <p className="font-poppins font-bold text-[13px] text-primary">
                            Date : {subscriptionDate}
                          </p>
                        )}
                      </div>
                      <button
                        onClick={() =>
                          setShowSubscriptionCalendar(!showSubscriptionCalendar)
                        }
                        className="px-3 py-1 bg-black text-white hover:opacity-90 transition-all font-nunito font-bold text-[12px] flex items-center gap-1"
                      >
                        {showSubscriptionCalendar ? (
                          <FiChevronUp />
                        ) : (
                          <FiChevronDown />
                        )}
                        Calendrier
                      </button>
                    </div>

                    <AnimatePresence>
                      {showSubscriptionCalendar && (
                        <motion.div
                          initial={{ opacity: 0, height: 0 }}
                          animate={{ opacity: 1, height: "auto" }}
                          exit={{ opacity: 0, height: 0 }}
                          className="mt-2 p-2 bg-white border border-gray-200 overflow-hidden"
                        >
                          <ReservationServiceCalendar
                            onContinue={handleSubscriptionDateUpdate}
                            servicesSelected={
                              selectedSubscriptionService.service
                            }
                            hideHeader={true}
                          />
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </div>
                )}
            </div>

            {/* Fixed Footer */}
            <div className="flex-none p-3 border-t flex gap-3">
              <button
                onClick={() => {
                  setShowSubscriptionModal(false);
                  setSubscriptionCode("");
                  setSubscriptionData(null);
                  setSubscriptionDate(null);
                  setSelectedSubscription(null);
                  setSelectedSubscriptionService(null);
                  setSelectedSubscriptionServiceCode("");
                  setAnonymousNom("");
                  setAnonymousPrenom("");
                  setAnonymousEmail("");
                  setAnonymousPhone("");
                }}
                className="flex-1 px-4 py-2 border-2 border-gray-300 text-gray-700 hover:bg-gray-50 transition-all duration-300 font-nunito font-bold text-sm"
              >
                Annuler
              </button>
              <button
                onClick={processSubscriptionReservation}
                disabled={
                  isSubmitting ||
                  (!selectedSubscriptionServiceCode && !subscriptionData) ||
                  !subscriptionDate ||
                  (!user &&
                    (!anonymousNom ||
                      !anonymousPrenom ||
                      !anonymousEmail ||
                      !anonymousPhone))
                }
                className="flex-[2] px-4 py-2 bg-gradient-to-r from-blue-500 to-blue-700 text-white hover:opacity-90 disabled:opacity-50 transition-all duration-300 font-nunito font-bold text-sm"
              >
                {isSubmitting ? (
                  <div className="flex items-center justify-center gap-2">
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                    En cours...
                  </div>
                ) : (
                  "Confirmer la réservation"
                )}
              </button>
            </div>
          </motion.div>
        </div>
      )}

      {/* Modal de choix de paiement (existant) */}
      {showPaymentModal && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-[100] p-4">
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-white max-w-md w-full h-[80vh] flex flex-col shadow-2xl overflow-hidden"
          >
            {/* Header */}
            <div className="flex-none p-3 border-b flex items-center justify-between">
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 bg-gradient-to-br from-secondary to-primary flex items-center justify-center">
                  <FiCreditCard className="text-white text-sm" />
                </div>
                <h3 className="font-poppins font-bold text-[15px] text-gray-800">
                  Option de paiement
                </h3>
              </div>
              <button
                onClick={() => setShowPaymentModal(false)}
                className="p-1 hover:bg-gray-100 transition-all duration-200"
              >
                <FiX size={18} className="text-gray-500" />
              </button>
            </div>

            {/* Scrollable Content */}
            <div className="flex-1 overflow-y-auto p-3 space-y-3">
              <p className="font-nunito font-light text-sm text-gray-600">
                Souhaitez-vous payer maintenant ou plus tard ?
              </p>

              {/* Formulaire pour utilisateurs anonymes */}
              <div className="space-y-3 p-3 bg-gray-50 border border-gray-200">
                <p className="font-poppins font-bold text-[14px] text-gray-800">
                  Vos informations
                </p>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                  <div>
                    <label className="block font-nunito font-bold text-[12px] text-gray-600 mb-1">
                      Nom *
                    </label>
                    <div className="relative">
                      <FiUser
                        className="absolute left-2 top-1/2 -translate-y-1/2 text-gray-400"
                        size={14}
                      />
                      <input
                        type="text"
                        value={anonymousNom}
                        onChange={(e) => setAnonymousNom(e.target.value)}
                        placeholder="Dupont"
                        className="w-full pl-8 pr-2 py-1.5 border border-gray-300 focus:border-primary focus:ring-1 focus:ring-primary transition-all font-nunito text-sm"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block font-nunito font-bold text-[12px] text-gray-600 mb-1">
                      Prénom *
                    </label>
                    <div className="relative">
                      <FiUser
                        className="absolute left-2 top-1/2 -translate-y-1/2 text-gray-400"
                        size={14}
                      />
                      <input
                        type="text"
                        value={anonymousPrenom}
                        onChange={(e) => setAnonymousPrenom(e.target.value)}
                        placeholder="Jean"
                        className="w-full pl-8 pr-2 py-1.5 border border-gray-300 focus:border-primary focus:ring-1 focus:ring-primary transition-all font-nunito text-sm"
                      />
                    </div>
                  </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                  <div>
                    <label className="block font-nunito font-bold text-[12px] text-gray-600 mb-1">
                      Email *
                    </label>
                    <div className="relative">
                      <FiMail
                        className="absolute left-2 top-1/2 -translate-y-1/2 text-gray-400"
                        size={14}
                      />
                      <input
                        type="email"
                        value={anonymousEmail}
                        onChange={(e) => setAnonymousEmail(e.target.value)}
                        placeholder="votre@email.com"
                        className="w-full pl-8 pr-2 py-1.5 border border-gray-300 focus:border-primary focus:ring-1 focus:ring-primary transition-all font-nunito text-sm"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block font-nunito font-bold text-[12px] text-gray-600 mb-1">
                      Téléphone *
                    </label>
                    <div className="relative">
                      <FiPhone
                        className="absolute left-2 top-1/2 -translate-y-1/2 text-gray-400"
                        size={14}
                      />
                      <input
                        type="tel"
                        value={anonymousPhone}
                        onChange={(e) => setAnonymousPhone(e.target.value)}
                        placeholder="0102030405"
                        className="w-full pl-8 pr-2 py-1.5 border border-gray-300 focus:border-primary focus:ring-1 focus:ring-primary transition-all font-nunito text-sm"
                      />
                    </div>
                  </div>
                </div>
              </div>
              {/* {!user && (
              )} */}

              <div className="bg-gradient-to-r from-gray-50 to-white p-3 border border-gray-200">
                <div className="flex justify-between items-center">
                  <span className="font-poppins font-bold text-[13px] text-gray-600 uppercase">
                    Total à payer
                  </span>
                  <span className="font-poppins font-bold text-[18px] text-primary">
                    {getTotalPrice()} FCFA
                  </span>
                </div>
              </div>
            </div>

            {/* Fixed Footer */}
            <div className="flex-none p-3 border-t flex flex-col gap-2">
              <button
                onClick={() => initializeFedapayPayment()}
                disabled={
                  isSubmitting ||
                  (!user &&
                    (!anonymousNom ||
                      !anonymousPrenom ||
                      !anonymousEmail ||
                      !anonymousPhone))
                }
                className="w-full px-4 py-2 bg-gradient-to-r from-primary to-secondary text-white hover:opacity-90 disabled:opacity-50 disabled:cursor-not-allowed transition-all font-nunito font-bold text-sm flex items-center justify-center gap-2 shadow-sm"
              >
                {isSubmitting ? (
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                ) : (
                  <FiCreditCard size={16} />
                )}
                {isSubmitting ? "Traitement..." : "Payer maintenant"}
              </button>

              <button
                onClick={() => handlePaymentChoice(false)}
                disabled={
                  isSubmitting ||
                  (!user &&
                    (!anonymousNom ||
                      !anonymousPrenom ||
                      !anonymousEmail ||
                      !anonymousPhone))
                }
                className="w-full px-4 py-2 border border-gray-300 text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-all font-nunito font-bold text-sm shadow-sm"
              >
                Payer plus tard (Salon)
              </button>

              <button
                onClick={() => setShowPaymentModal(false)}
                className="w-full py-1 text-gray-500 hover:text-gray-700 transition-all font-nunito font-bold text-sm"
              >
                Annuler
              </button>
            </div>
          </motion.div>
        </div>
      )}

      {/* Modal FedaPay (existant) */}
      {showFedapayModal && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-[100] p-4">
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-white max-w-md w-full max-h-[70vh] flex flex-col shadow-2xl overflow-hidden"
          >
            {/* Header */}
            <div className="flex-none p-3 border-b flex items-center justify-center">
              <h3 className="font-poppins font-bold text-[15px] text-gray-800">
                Paiement sécurisé FedaPay
              </h3>
            </div>

            {/* Content */}
            <div className="flex-1 overflow-y-auto p-4 space-y-4">
              <div className="bg-gradient-to-r from-gray-50 to-white p-4 border border-gray-200">
                <div className="text-center">
                  <div className="w-16 h-16 bg-gradient-to-br from-secondary to-primary rounded-full flex items-center justify-center mx-auto mb-4 shadow-lg">
                    <FiCreditCard className="text-white text-2xl" />
                  </div>
                  <p className="font-nunito font-light text-sm text-gray-600 mb-1">
                    Montant à payer
                  </p>
                  <p className="font-poppins font-bold text-[24px] bg-gradient-to-r from-primary to-secondary bg-clip-text text-transparent">
                    {getTotalPrice()} FCFA
                  </p>
                </div>
              </div>

              <div className="space-y-4">
                <p className="font-nunito font-light text-sm text-gray-600 text-center">
                  Préparation du paiement en cours...
                </p>
                <div className="flex justify-center">
                  <div className="w-8 h-8 border-4 border-secondary border-t-transparent rounded-full animate-spin"></div>
                </div>
              </div>
            </div>

            {/* Footer */}
            <div className="flex-none p-3 border-t">
              <button
                onClick={initializeFedapayPayment}
                disabled={isSubmitting}
                className="w-full px-4 py-3 bg-gradient-to-r from-primary to-secondary text-white hover:opacity-90 disabled:opacity-50 transition-all font-nunito font-bold text-sm flex items-center justify-center gap-2"
              >
                {isSubmitting ? (
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                ) : (
                  <FiCreditCard size={18} />
                )}
                {isSubmitting ? "Traitement..." : "Payer maintenant"}
              </button>
            </div>
          </motion.div>
        </div>
      )}
    </>
  );
}

export default CartPage;
